import { DAY_INDICES } from '../day-part/constants';
import type { DayIndex } from '../day-part/types';
import { hhmmToMinutes } from '../day-part/utils';

export type SDMAnalyticsProperties = Record<string, string | number | boolean | undefined>;

export interface AnalyticsWidgetLike {
  readonly config?: { readonly type?: string; readonly horizon?: string };
  readonly thresholds?: readonly { readonly dayPartsId?: string }[];
}

export interface AnalyticsLayoutLike {
  readonly id?: string;
  readonly name?: string;
  readonly widgets?: readonly AnalyticsWidgetLike[];
}

export type DayPartsLike = Partial<
  Record<DayIndex, readonly { readonly start: string; readonly end: string }[]>
>;

export interface AnalyticsDayPartLike {
  readonly id?: string;
  readonly label?: string;
  readonly dayParts?: DayPartsLike;
}

type WidgetLike = AnalyticsWidgetLike;
type WidgetList = readonly WidgetLike[];

export const WIDGET_TYPE_PROPERTY_PREFIX = 'widgetsOfType';

const DAY_PART_HORIZON = 'currentDayPart';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getWidgetTypeCounts(widgets: WidgetList): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const widget of widgets) {
    const type = widget?.config?.type;
    if (!type) continue;
    counts[type] = (counts[type] ?? 0) + 1;
  }
  return counts;
}

export function toWidgetTypeProperties(counts: Record<string, number>): Record<string, number> {
  const properties: Record<string, number> = {};
  for (const [type, count] of Object.entries(counts)) {
    properties[`${WIDGET_TYPE_PROPERTY_PREFIX}${type}`] = count;
  }
  return properties;
}

export function widgetHasDayPartThreshold(widget: WidgetLike): boolean {
  return (widget?.thresholds ?? []).some((threshold) => Boolean(threshold?.dayPartsId));
}

export function widgetUsesDayPartHorizon(widget: WidgetLike): boolean {
  return widget?.config?.horizon === DAY_PART_HORIZON;
}

/**
 * A widget uses day parts only when it carries a day-part threshold override.
 */
export function widgetUsesDayParts(widget: WidgetLike): boolean {
  return widgetHasDayPartThreshold(widget);
}

/**
 * Widget asks for the `currentDayPart` horizon but has no threshold override to resolve a
 * day part from, so it silently shows a rolling 1h number instead.
 *
 * The widget dialog warns about this (`currentDayPartNoAssociationWarning`) but does not
 * block saving, so the state reaches production. Counting it separately keeps it out of the
 * adoption numbers while still surfacing it — this is intent to use day parts without the
 * configuration that makes them work, which is a product problem worth seeing.
 */
export function widgetHasDayPartHorizonOnly(widget: WidgetLike): boolean {
  return widgetUsesDayPartHorizon(widget) && !widgetHasDayPartThreshold(widget);
}

/**
 * Builds a predicate for "this widget references a day part that still exists".
 *
 * A widget threshold can outlive the day part it points at: deleting a day part invalidates
 * only the day parts cache, not the layouts cache, so the layouts read here can still carry
 * references to a day part that is gone. At runtime `getResolvedThreshold` finds no matching
 * day part for such a reference and falls back, so an orphaned reference is not day part usage.
 *
 * Returns a single-argument predicate on purpose — it is safe to pass straight to
 * `Array.prototype.filter`, which would otherwise supply the index as a second argument.
 */
export function makeWidgetUsesLiveDayPart(
  liveDayPartIds: ReadonlySet<string>,
): (widget: WidgetLike) => boolean {
  return (widget) =>
    (widget?.thresholds ?? []).some((threshold) => {
      const id = threshold?.dayPartsId;
      return id !== undefined && liveDayPartIds.has(id);
    });
}

/** Ids of the day parts that currently exist, for orphan-reference filtering. */
export function collectLiveDayPartIds(dayParts: ReadonlyArray<AnalyticsDayPartLike>): Set<string> {
  return new Set(dayParts.map((dayPart) => dayPart?.id).filter((id): id is string => !!id));
}

function collectDayPartIds(widgets: WidgetList): Set<string> {
  const ids = new Set<string>();
  for (const widget of widgets) {
    for (const threshold of widget?.thresholds ?? []) {
      if (threshold?.dayPartsId) ids.add(threshold.dayPartsId);
    }
  }
  return ids;
}

function formatTypesUsed(counts: Record<string, number>): string {
  return Object.keys(counts).sort().join(',');
}

export function getDayPartScheduleMetrics(dayParts: DayPartsLike | undefined): {
  activeDayCount: number;
  scheduledMinutesPerWeek: number;
} {
  let activeDayCount = 0;
  let scheduledMinutesPerWeek = 0;

  for (const day of DAY_INDICES) {
    const ranges = dayParts?.[day] ?? [];
    if (ranges.length === 0) continue;
    activeDayCount += 1;
    for (const range of ranges) {
      scheduledMinutesPerWeek += Math.max(0, hhmmToMinutes(range.end) - hhmmToMinutes(range.start));
    }
  }

  return { activeDayCount, scheduledMinutesPerWeek };
}

/** Analytics properties describing a single saved day part. */
export function getDayPartAnalytics(dayPart: AnalyticsDayPartLike): SDMAnalyticsProperties {
  const { activeDayCount, scheduledMinutesPerWeek } = getDayPartScheduleMetrics(dayPart.dayParts);

  return {
    dayPartId: dayPart.id,
    dayPartLabel: dayPart.label,
    dayPartActiveDayCount: activeDayCount,
    dayPartScheduledHoursPerWeek: round2(scheduledMinutesPerWeek / 60),
  };
}

/**
 * Per-layout adoption metrics. Emitted once per layout so `widgetCount` can be charted
 * as a distribution (`widgets_per_layout`) rather than only as a customer-level average.
 */
export function getLayoutAdoptionMetrics(
  layout: AnalyticsLayoutLike,
  /**
   * When supplied, threshold references to day parts that no longer exist are ignored, so the
   * counts reflect real usage. Callers that do not have the day part list loaded (the layout
   * save and delete events) omit it and get raw reference counts.
   */
  liveDayPartIds?: ReadonlySet<string>,
): SDMAnalyticsProperties {
  const widgets = layout?.widgets ?? [];
  const typeCounts = getWidgetTypeCounts(widgets);
  const usesDayPart = liveDayPartIds
    ? makeWidgetUsesLiveDayPart(liveDayPartIds)
    : widgetHasDayPartThreshold;
  const referencedDayPartIds = [...collectDayPartIds(widgets)].filter(
    (id) => !liveDayPartIds || liveDayPartIds.has(id),
  );
  const widgetsWithDayPartThreshold = widgets.filter(usesDayPart).length;
  const widgetsUsingDayPartHorizon = widgets.filter(widgetUsesDayPartHorizon).length;
  const widgetsWithDayPartHorizonOnly = widgets.filter(widgetHasDayPartHorizonOnly).length;

  return {
    layoutId: layout?.id,
    layoutName: layout?.name,
    widgetCount: widgets.length,
    isEmptyLayout: widgets.length === 0,
    widgetTypesUsed: formatTypesUsed(typeCounts),
    distinctWidgetTypeCount: Object.keys(typeCounts).length,
    ...toWidgetTypeProperties(typeCounts),
    dayPartsReferencedCount: referencedDayPartIds.length,
    widgetsWithDayPartThresholdCount: widgetsWithDayPartThreshold,
    widgetsUsingDayPartHorizonCount: widgetsUsingDayPartHorizon,
    /** Horizon set to `currentDayPart` with no threshold to resolve — silently inert. */
    widgetsWithDayPartHorizonOnlyCount: widgetsWithDayPartHorizonOnly,
    hasActiveDayPartInWidget: widgetsWithDayPartThreshold > 0,
  };
}

/**
 * Customer-level adoption census, computed from everything the customer has
 * saved.
 */
export function getSDMAdoptionMetrics(
  layouts: ReadonlyArray<AnalyticsLayoutLike>,
  dayParts: ReadonlyArray<AnalyticsDayPartLike>,
): SDMAnalyticsProperties {
  const allWidgets = layouts.flatMap((layout) => [...(layout?.widgets ?? [])]);
  const widgetCounts = layouts.map((layout) => layout?.widgets?.length ?? 0);
  const typeCounts = getWidgetTypeCounts(allWidgets);

  // Widget thresholds can reference a deleted day part, so every day part count below is
  // filtered to the day parts that still exist. Counting raw references would inflate the
  // assigned count and drive unusedDayPartCount too low.
  const liveDayPartIds = collectLiveDayPartIds(dayParts);
  const usesLiveDayPart = makeWidgetUsesLiveDayPart(liveDayPartIds);

  const widgetsWithDayPartThreshold = allWidgets.filter(usesLiveDayPart).length;
  const widgetsUsingDayPartHorizon = allWidgets.filter(widgetUsesDayPartHorizon).length;
  const widgetsWithDayPartHorizonOnly = allWidgets.filter(widgetHasDayPartHorizonOnly).length;

  const referencedDayPartIds = collectDayPartIds(allWidgets);
  const assignedDayPartIds = new Set(
    [...referencedDayPartIds].filter((id) => liveDayPartIds.has(id)),
  );
  const orphanDayPartRefCount = referencedDayPartIds.size - assignedDayPartIds.size;
  const layoutsUsingDayPartsCount = layouts.filter((layout) =>
    (layout?.widgets ?? []).some(usesLiveDayPart),
  ).length;

  const scheduleTotals = dayParts.reduce(
    (acc, dayPart) => {
      const { activeDayCount, scheduledMinutesPerWeek } = getDayPartScheduleMetrics(
        dayPart?.dayParts,
      );
      acc.activeDays += activeDayCount;
      acc.minutes += scheduledMinutesPerWeek;
      return acc;
    },
    { activeDays: 0, minutes: 0 },
  );

  return {
    // --- Layouts -----------------------------------------------------------
    /** layouts_per_customer */
    layoutCount: layouts.length,

    // --- Widgets -----------------------------------------------------------
    widgetCount: allWidgets.length,
    avgWidgetsPerLayout: layouts.length ? round2(allWidgets.length / layouts.length) : 0,
    maxWidgetsPerLayout: widgetCounts.length ? Math.max(...widgetCounts) : 0,
    minWidgetsPerLayout: widgetCounts.length ? Math.min(...widgetCounts) : 0,
    emptyLayoutCount: widgetCounts.filter((count) => count === 0).length,

    /** widget_type_distribution */
    widgetTypesUsed: formatTypesUsed(typeCounts),
    distinctWidgetTypeCount: Object.keys(typeCounts).length,
    ...toWidgetTypeProperties(typeCounts),

    // --- Day parts ---------------------------------------------------------
    /** day_parts_configured_per_customer */
    dayPartCount: dayParts.length,
    hasDayPartsConfigured: dayParts.length > 0,
    dayPartsAssignedToWidgetCount: assignedDayPartIds.size,
    // assignedDayPartIds is a subset of the live day parts, so this cannot go negative.
    unusedDayPartCount: dayParts.length - assignedDayPartIds.size,
    /**
     * Widget thresholds pointing at a day part that no longer exists. Excluded from the
     * counts above; surfaced so a non-zero value is visible rather than silently dropped.
     */
    orphanDayPartRefCount,
    avgActiveDaysPerDayPart: dayParts.length
      ? round2(scheduleTotals.activeDays / dayParts.length)
      : 0,
    totalScheduledDayPartHoursPerWeek: round2(scheduleTotals.minutes / 60),

    /**
     * The number of widgets genuinely using day parts: a threshold override is the only
     * configuration the runtime resolves a day part from.
     */
    widgetsWithDayPartThresholdCount: widgetsWithDayPartThreshold,
    widgetsUsingDayPartHorizonCount: widgetsUsingDayPartHorizon,
    /**
     * Misconfiguration signal, deliberately excluded from the adoption numbers: horizon set
     * to `currentDayPart` with no threshold to resolve, so the widget silently shows a 1h
     * rolling number. A non-zero count here means customers are reaching for day parts and
     * not getting them.
     */
    widgetsWithDayPartHorizonOnlyCount: widgetsWithDayPartHorizonOnly,
    hasDayPartHorizonOnlyWidget: widgetsWithDayPartHorizonOnly > 0,
    layoutsUsingDayPartsCount,
    /** customers_with_active_day_parts_in_widget — distinct Customer Id where this is true. */
    hasActiveDayPartInWidget: widgetsWithDayPartThreshold > 0,
  };
}

export function getAdoptionSnapshotSignature(
  customerId: string | undefined,
  metrics: SDMAnalyticsProperties,
  /**
   * Per-layout property bags emitted alongside the census. These must be part of the
   * signature: the census is customer-level aggregates only, so a layout rename or a widget
   * reshuffle between layouts can leave every aggregate identical while the per-layout rows
   * change. Omitting them would suppress the emission and leave the newest layout rows stale.
   */
  layoutMetrics: ReadonlyArray<SDMAnalyticsProperties> = [],
): string {
  const serialise = (properties: SDMAnalyticsProperties) =>
    Object.keys(properties)
      .sort()
      .map((key) => `${key}=${String(properties[key])}`)
      .join('|');

  // Sorted so that a reordering of the layouts list, with no content change, does not read
  // as a change and emit a redundant snapshot.
  const layoutPart = layoutMetrics.map(serialise).sort().join('||');

  return `${customerId ?? 'unknown'}::${serialise(metrics)}::${layoutPart}`;
}
